import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom';
import Layout2 from "@/components/Layout/Layout2";
import { onlineOrderingLinks } from "@/components/lib/SidebarLinks";
import LeftBar2 from "@/components/Layout/LeftBar2";
import PathMap from '@/components/lib/PathMap';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Eye, PenLine, Trash2 } from "lucide-react";
import { FaDownload } from "react-icons/fa6";
import { Switch } from '@/components/ui/switch';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAllSmartQrCode,
  deleteSmartQrCodeById,
  updateToggleSmartQrCode,
} from '@/redux/slice/order-ordering-slice/smart-qr-codes-slice';
import useAuth from '@/hooks/useAuth';
import { toast } from "react-hot-toast";
import QRCode from 'react-qr-code';
import Pagination from '@/components/lib/Pagination';
import useDebounce from '@/hooks/useDebounce';
import useSettings from '@/hooks/useSettings';

const SmartQRCodeOrder = () => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [status, setStatus] = useState("all");
  const { settings } = useSettings();
  
  const { smartQrCodes, getLoading, pagination } = useSelector(
    (state) => state.onlineOrdering.smartQrCodes
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, status]);

  useEffect(() => {
    if (user?.restaurant_id) {
      dispatch(fetchAllSmartQrCode({ 
        restaurant_id: user.restaurant_id, 
        page: currentPage,
        per_page: perPage,
        search_term: debouncedSearchTerm,
        status: status
      }))
        .unwrap()
        .catch(err => {
          toast.error(err || 'Failed to fetch Smart QR Codes');
        });
    }
  }, [dispatch, user?.restaurant_id, currentPage, perPage, debouncedSearchTerm, status]);

  const handlePerPageChange = (val) => {
    setPerPage(Number(val));
    setCurrentPage(1);
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this smart QR code?")) {
      dispatch(deleteSmartQrCodeById(id))
        .unwrap()
        .then(() => toast.success("Deleted successfully"))
        .catch((err) => toast.error(err || "Failed to delete"));
    }
  };

  const handleToggle = (id, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    dispatch(updateToggleSmartQrCode({ id, status: newStatus }))
      .unwrap()
      .then(() => toast.success("Status updated successfully"))
      .catch((err) => toast.error(err || "Failed to update status"));
  };

  const downloadQrcodeaspng = (id, name) => {
    const svgElement = document.getElementById(id);
    if (!svgElement) return;

    const clonedSvg = svgElement.cloneNode(true);
    clonedSvg.removeAttribute("style");

    const svgString = new XMLSerializer().serializeToString(clonedSvg);
    const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const URL = window.URL || window.webkitURL || window;
    const blobURL = URL.createObjectURL(svgBlob);

    const image = new Image();
    image.onload = () => {
        const canvas = document.createElement("canvas");
        const size = 1024;
        canvas.width = size;
        canvas.height = size;
        const context = canvas.getContext("2d");
        
        if (context) {
            context.fillStyle = "#ffffff";
            context.fillRect(0, 0, size, size);
            
            const margin = 128;
            const qrSize = size - 2 * margin;
            context.drawImage(image, margin, margin, qrSize, qrSize);
            
            const pngURL = canvas.toDataURL("image/png");
            const downloadLink = document.createElement("a");
            downloadLink.href = pngURL;
            downloadLink.download = `${name || 'smart'}-qr-code.png`;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
        }
        URL.revokeObjectURL(blobURL);
    };
    image.src = blobURL;
  };

  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={onlineOrderingLinks} />}
    >
      <div className="container">
        <div className='flex justify-between items-center'>
          <PathMap 
            pageName={{ title: "SMART QR Code Order" }}
            paths={[
              // { name: "Home", path: "/" },
              // { name: "Online Ordering", path: "/online-ordering" },
              { name: "QR Code Ordering", path: "/onlineordering/qr-code/general" },
              { name: "SMART QR Code" },
            ]}
          />
        </div> 
         <div className='flex justify-center items-center mt-5'>
             <div className='w-full flex items-center justify-between'>
                 <div className="flex gap-4 items-center">
                   <div className="flex gap-2 justify-center items-center">
                     <h3 className="text-white text-sm">Show</h3>
                     <Select
                       onValueChange={handlePerPageChange}
                       value={perPage.toString()}
                     >
                       <SelectTrigger className="w-[70px] bg-cblack3 border-gray-600 h-9">
                         <SelectValue placeholder="10" />
                       </SelectTrigger>
                       <SelectContent>
                         <SelectGroup>
                           <SelectItem value="3">3</SelectItem>
                           <SelectItem value="5">5</SelectItem>
                           <SelectItem value="10">10</SelectItem>
                           <SelectItem value="20">20</SelectItem>
                           <SelectItem value="30">30</SelectItem>
                         </SelectGroup>
                       </SelectContent>
                     </Select>
                   </div>
                   
                   {/* Search */}
                   <input 
                     type="text" 
                     placeholder="Search..."
                     className="bg-cblack3 text-white text-sm rounded-md border border-gray-600 outline-none px-3 h-9 w-[200px]"
                     value={searchTerm}
                     onChange={(e) => setSearchTerm(e.target.value)}
                   />

                   {/* Status */}
                   <Select onValueChange={(val) => setStatus(val)} value={status}>
                     <SelectTrigger className="w-[120px] bg-cblack3 border-gray-600 h-9">
                       <SelectValue placeholder="Status" />
                     </SelectTrigger>
                     <SelectContent>
                       <SelectGroup>
                         <SelectItem value="all">All</SelectItem>
                         <SelectItem value="active">Active</SelectItem>
                         <SelectItem value="inactive">Inactive</SelectItem>
                       </SelectGroup>
                     </SelectContent>
                   </Select>
                 </div>

                 <Link to="/onlineordering/qr-code/add-smart-ordering" className='bg-color6 py-1.5 px-3 rounded-md cursor-pointer text-color1 text-sm font-medium'>
                   New Smart QR
                 </Link>
             </div>
         </div>
        <div className="overflow-x-auto overflow-y-hidden mt-4">
          <Table className="w-full">
            <TableHeader>
              <TableRow className=" hover:bg-transparent">
                <TableHead className="text-white whitespace-nowrap">
                  Name
                </TableHead>
                <TableHead className="text-white whitespace-nowrap">
                  Items
                </TableHead>
                <TableHead className="text-white whitespace-nowrap">
                  Price
                </TableHead>
                <TableHead className="text-white whitespace-nowrap">
                  Discount Price
                </TableHead>
                <TableHead className="text-white whitespace-nowrap">
                  Status
                </TableHead>
                <TableHead className="text-white whitespace-nowrap">
                  QR Code
                </TableHead>
                <TableHead className="text-right text-white whitespace-nowrap">
                  Action
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {getLoading ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <div className="py-6 text-center text-white/70">
                      Loading...
                    </div>
                  </TableCell>
                </TableRow>
              ) : smartQrCodes?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <div className="py-6 text-center text-white/70">
                      Data not found.
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                smartQrCodes?.map((order) => {
                  return (
                    <TableRow key={order?.id} className=" hover:bg-white/5">
                      <TableCell className="max-w-[160px]">
                        <div className="truncate text-white" title={order?.name}>
                          {order?.name}
                        </div>
                        {order?.description && (
                          <div className="text-xs text-white/70 truncate" title={order?.description}>
                            {order?.description}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-white whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span>{order?.items?.length ?? 0}</span>
                          <Dialog>
                            <DialogTrigger asChild>
                              <button className="text-cgreen hover:text-cgreen/80 transition-colors" title="View Items">
                                <Eye size={18} />
                              </button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-xl">
                              <DialogHeader>
                                <DialogTitle className="text-left">Items for {order?.name}</DialogTitle>
                              </DialogHeader>
                              <div className="mt-4 max-h-[60vh] overflow-y-auto">
                                {order?.items?.length > 0 ? (
                                  <Table>
                                    <TableHeader>
                                      <TableRow className="border-white/10">
                                        <TableHead className="text-white">Item Name</TableHead>
                                        <TableHead className="text-white">Image</TableHead>
                                        <TableHead className="text-white">Quantity</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {order.items.map((item, index) => (
                                        <TableRow key={index} className="hover:bg-white/5 border-white/10">
                                          <TableCell className="text-white">
                                            {item.item_name}
                                            {item.item_type && (
                                              <span className="ml-2 text-[10px] text-white/60 bg-white/10 px-1.5 py-0.5 rounded capitalize">
                                                {item.item_type?.replace(/_/g, " ")}
                                              </span>
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            {item.file_manager_url ? (
                                              <img 
                                                src={item.file_manager_url} 
                                                alt={item.item_name} 
                                                className="w-10 h-10 object-cover rounded"
                                              />
                                            ) : (
                                              <span className="text-white/60 text-[10px] bg-white/10 px-2 py-1 rounded">No Image</span>
                                            )}
                                          </TableCell>
                                          <TableCell className="text-white">
                                            {item.quantity ?? 1}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                ) : (
                                  <div className="text-white/60 text-center py-4">No items added</div>
                                )}
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </TableCell>
                      <TableCell className="text-white whitespace-nowrap">
                        £{order?.price}
                      </TableCell>
                      <TableCell className="text-white whitespace-nowrap">
                        {order?.discount_price ? `£${order?.discount_price}` : "-"}
                      </TableCell>
                      <TableCell className="text-white">
                        <Switch
                          checked={order?.status === "active"}
                          onCheckedChange={() => handleToggle(order?.id, order?.status)}
                        />
                      </TableCell>
                      <TableCell className="text-white">
                         <Dialog>
                           <DialogTrigger asChild>
                             <div className="bg-white p-1 rounded inline-block cursor-pointer hover:opacity-80 transition-opacity">
                               <QRCode 
                                 value={`${settings?.restaurant?.website}/qr/${order?.unique_id}`} 
                                 size={40} 
                               />
                             </div>
                           </DialogTrigger>
                           <DialogContent className="sm:max-w-md flex flex-col items-center justify-center">
                             <DialogHeader>
                               <DialogTitle className="text-center">{order?.name} QR Code</DialogTitle>
                             </DialogHeader>
                             <div className="flex flex-col items-center justify-center mt-6">
                               <QRCode
                                 id={`qr-code-${order?.unique_id}`}
                                 size={256}
                                 style={{ height: "auto", width: "256px", padding: '16px', background: 'white' }}
                                 value={`${settings?.restaurant?.website}/qr/${order?.unique_id}`}
                                 viewBox={`0 0 256 256`}
                               />
                               <div 
                                 onClick={() => downloadQrcodeaspng(`qr-code-${order?.id}`, order?.name)} 
                                 className="w-[256px] py-2 flex items-center justify-center gap-2 mt-6 cursor-pointer bg-color6 rounded-md text-color1 hover:bg-color6/90 transition-colors"
                               >
                                 <span>Download</span>
                                 <FaDownload />
                               </div>
                             </div>
                           </DialogContent>
                         </Dialog>
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end mr-2 gap-3 text-white items-center">
                          <Link
                            to={`/onlineordering/qr-code/edit-smart-ordering/${order?.id}`}
                            className="transition-colors text-cgreen hover:text-cgreen/80"
                          >
                            <PenLine size={18} />
                          </Link>
                          <button
                            onClick={() => handleDelete(order?.id)}
                            className="transition-colors text-cred hover:text-cred/80"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          
          {pagination && pagination.last_page > 1 && (
            <div className="flex justify-end mt-4">
              <Pagination
                currentPage={currentPage}
                totalPages={pagination.last_page}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          )}
        </div>
      </div>
    </Layout2>
  )
}

export default SmartQRCodeOrder;